import { GraphQLClient } from 'graphql-request'
import LogRocket from 'src/logrocket/logrocket'
import { NOTIFICATIONS, PERSIST_ACCOUNT } from 'src/redux/middleware/user'
import { initialState } from 'src/redux/reducers/user'
import { Store } from 'src/redux/store'
import { SALEOR_ACCESS_TOKEN_KEY } from 'src/saleor/utils/auth'

export const useFormDataHeader = 'X-Use-Form-Data'
export const baseURL = process.env.REACT_APP_API_URL || ''
export const LEGACY_SITE_URL =
  process.env.REACT_APP_OLD_WEBSITE_URL || 'https://www.sendoutcards.com'

export const asFormData = <Variables = undefined>(operation: {
  query: string
  variables: Variables
}) => {
  const data = new FormData()
  data.append('query', operation.query)

  if (operation.variables) {
    Object.entries(operation.variables).forEach(([key, value]) => {
      if (value instanceof Blob) {
        data.append(key, value)
      } else {
        data.append(key, value as string)
      }
    })
  }

  return data
}
// graphql-request 7 will require an absolute URL.
// Note that document.baseURI is only used if the provided URL is relative.
const graphqlUrl = new URL(`${baseURL}/graphql`, document.baseURI).href
export const publicGraphqlClient = new GraphQLClient(graphqlUrl, {
  method: 'GET',
})
export const graphqlClient = new GraphQLClient(graphqlUrl, {
  headers: () => {
    const token = Store.getState().user.controlData.token
    const sessionURL =
      process.env.NODE_ENV === 'production' && LogRocket.sessionURL
    return {
      Authorization: `Token ${token}`,
      ...(sessionURL ? { 'X-LogRocket-URL': sessionURL } : {}),
    }
  },
  requestMiddleware: request => {
    if (
      process.env.NODE_ENV === 'development' &&
      process.env.REACT_APP_USE_GQL_DEBUG === 'true'
    ) {
      const regex = /www(?!\.prod)/
      const url = request.url
        .replace(regex, 'www.prod')
        .replace('graphql', 'graphiql')
      const hash = Object.entries({
        query: JSON.parse(request.body as string).query,
        variables: request.variables,
        headers: request.headers,
      })
        .filter(([, value]) => value)
        .map(
          ([key, value]) =>
            `${key}=${encodeURIComponent(
              typeof value === 'string' ? value : JSON.stringify(value),
            )}`,
        )
        .join('&')
      console.log(
        '[soc-graphql-debugger-request]',
        request.operationName,
        `variables=${JSON.stringify(request.variables)}`,
        `${url}#${hash}`,
      )
    }

    // Our own patch for file upload using GraphQL
    // When we move to the latest version, we can try the upload extension, but we would also need to update backend
    if (request.headers && useFormDataHeader in request.headers) {
      // This is a header we just use to signal that this request should be sent as form data
      delete request.headers[useFormDataHeader]
      // We need to delete 'Content-Type', otherwise graphql-request will set it as application/json
      delete request.headers['Content-Type']
      const formData = asFormData({
        query: JSON.parse(request.body as string).query,
        variables: request.variables,
      })

      request.body = formData
    }

    return request
  },
  responseMiddleware: response => {
    if (response instanceof Error) {
      console.error('[soc-graphql-error]', `${response.message}\n`)

      LogRocket.captureException(response)

      const isInvalidToken = response.message.startsWith('Invalid token.')
      const isUserInactiveOrDeleted = response.message.startsWith(
        'User inactive or deleted.',
      )

      if (isInvalidToken || isUserInactiveOrDeleted) {
        try {
          sessionStorage.removeItem(NOTIFICATIONS)
          localStorage.removeItem(SALEOR_ACCESS_TOKEN_KEY)
          localStorage.setItem(
            PERSIST_ACCOUNT,
            JSON.stringify({
              ...initialState,
              controlData: {
                ...initialState.controlData,
                token: undefined,
              },
            }),
          )
        } catch (error) {
          console.error('[soc-graphql-error]', error)
          if (error instanceof Error) {
            LogRocket.captureException(error as Error)
          }
        } finally {
          const url = new URL(window.location.href)
          url.searchParams.delete('access_token')
          window.history.replaceState({}, '', url.toString())
          window.location.reload()
        }
      }
    } else {
      if (response.errors !== undefined && response.errors.length !== 0) {
        console.error('[soc-graphql-errors]', response.errors)
      }
      if (
        process.env.NODE_ENV === 'development' &&
        process.env.REACT_APP_USE_GQL_RESPONSE_DEBUG === 'true'
      ) {
        console.log('[soc-graphql-debugger-response]', response.data)
      }
    }
  },
})
